import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Link,
  Tooltip,
  IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import { CustomColumnDefinition } from '../../types/lead';
import { resolveOptionLabel, parseCustomFields } from '../../utils/leads/leadsUtils';
import { format12HourTime, formatCustomDate, formatCustomDateTime } from '../../utils/dateUtils';

interface KeyValueRowProps {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  copyableText?: string;
  isLast?: boolean;
}

const KeyValueRow: React.FC<KeyValueRowProps> = ({
  label,
  value,
  icon,
  copyableText,
  isLast = false,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (copyableText) {
      navigator.clipboard.writeText(copyableText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '130px 1fr', sm: '160px 1fr' },
        gap: 1.5,
        alignItems: 'center',
        py: 1.25,
        px: 2,
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: 'grey.50',
        },
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {icon && (
          <Box sx={{ color: 'text.secondary', display: 'flex', fontSize: 18, flexShrink: 0 }}>
            {icon}
          </Box>
        )}
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={500}
          noWrap
          title={label}
        >
          {label}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          minWidth: 0,
        }}
      >
        <Box sx={{ wordBreak: 'break-word', flexGrow: 1, overflowWrap: 'anywhere' }}>
          {typeof value === 'string' || typeof value === 'number' ? (
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              {value}
            </Typography>
          ) : (
            value
          )}
        </Box>

        {copyableText && (
          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{ p: 0.5, color: copied ? 'success.main' : 'text.secondary', flexShrink: 0 }}
            >
              {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const renderCustomFieldValue = (value: any, matchedCol?: CustomColumnDefinition) => {
  if (value === undefined || value === null || value === '') {
    return (
      <Typography variant="body2" color="text.disabled" fontStyle="italic">
        —
      </Typography>
    );
  }

  const colType = matchedCol?.type;

  if (colType === 'boolean' || typeof value === 'boolean') {
    const isTrue = value === true || value === 'true' || value === 1 || value === '1';
    return (
      <Chip
        label={isTrue ? 'Yes' : 'No'}
        size="small"
        color={isTrue ? 'primary' : 'default'}
        variant="outlined"
        sx={{ height: 22, fontSize: '0.75rem' }}
      />
    );
  }

  if (colType === 'multi_select' || Array.isArray(value)) {
    let arr: any[] = [];
    if (Array.isArray(value)) {
      arr = value;
    } else if (typeof value === 'string' && value.trim() !== '') {
      const trimmed = value.trim();
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) arr = parsed;
          else arr = [parsed];
        } catch (e) {
          arr = trimmed.split(',').map((s) => s.trim());
        }
      } else {
        arr = trimmed.split(',').map((s) => s.trim());
      }
    } else {
      arr = [value];
    }

    if (arr.length === 0) return <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {arr.map((item, idx) => (
          <Chip
            key={idx}
            label={resolveOptionLabel(item, matchedCol?.options)}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.75rem' }}
          />
        ))}
      </Box>
    );
  }

  if (colType === 'date') {
    return (
      <Typography variant="body2" color="text.primary" fontWeight={500}>
        {formatCustomDate(value)}
      </Typography>
    );
  }

  if (colType === 'datetime') {
    return (
      <Typography variant="body2" color="text.primary" fontWeight={500}>
        {formatCustomDateTime(value)}
      </Typography>
    );
  }

  if (colType === 'time') {
    return (
      <Typography variant="body2" color="text.primary" fontWeight={500}>
        {format12HourTime(value)}
      </Typography>
    );
  }

  if (colType === 'email' || (typeof value === 'string' && value.includes('@') && value.includes('.'))) {
    return (
      <Link href={`mailto:${value}`} underline="hover" color="primary" variant="body2" fontWeight={500}>
        {String(value)}
      </Link>
    );
  }

  if (colType === 'url' || (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://')))) {
    return (
      <Link href={String(value)} target="_blank" rel="noopener noreferrer" underline="hover" color="primary" variant="body2" fontWeight={500}>
        {String(value)}
      </Link>
    );
  }

  if (colType === 'select') {
    return (
      <Chip label={resolveOptionLabel(value, matchedCol?.options)} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.75rem', fontWeight: 500 }} />
    );
  }

  return (
    <Typography variant="body2" color="text.primary" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
      {String(value)}
    </Typography>
  );
};

export interface LeadDetailsViewProps {
  existingLead: {
    name?: string;
    phone?: string;
    status?: string;
    notes?: string;
    custom_fields?: Record<string, any>;
  };
  notes?: string;
  customFields?: Record<string, any>;
  customColumns?: CustomColumnDefinition[];
}

export const LeadDetailsView: React.FC<LeadDetailsViewProps> = ({
  existingLead,
  notes: propNotes,
  customFields: propCustomFields,
  customColumns = [],
}) => {
  const displayNotes = propNotes !== undefined ? propNotes : existingLead.notes || '';
  const displayCustomFields = parseCustomFields(propCustomFields !== undefined ? propCustomFields : existingLead.custom_fields);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Basic Information Section */}
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight="bold"
          letterSpacing={0.8}
          sx={{ display: 'block', mb: 1, textTransform: 'uppercase', fontSize: '0.75rem' }}
        >
          Basic Information
        </Typography>
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <KeyValueRow
            label="Lead Name"
            icon={<PersonOutlineIcon />}
            value={
              existingLead.name ? (
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {existingLead.name}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.disabled" fontStyle="italic">
                  No name set
                </Typography>
              )
            }
          />
          <KeyValueRow
            label="Phone Number"
            icon={<PhoneOutlinedIcon />}
            copyableText={
              existingLead.phone
                ? existingLead.phone.startsWith('+')
                  ? existingLead.phone
                  : `+${existingLead.phone}`
                : undefined
            }
            value={
              existingLead.phone ? (
                <Link
                  href={`tel:${existingLead.phone.startsWith('+') ? existingLead.phone : `+${existingLead.phone}`}`}
                  underline="hover"
                  color="primary"
                  variant="body2"
                  fontWeight={500}
                >
                  {existingLead.phone.startsWith('+') ? existingLead.phone : `+${existingLead.phone}`}
                </Link>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  N/A
                </Typography>
              )
            }
          />
          <KeyValueRow
            label="Lead Status"
            icon={<LabelOutlinedIcon />}
            isLast
            value={
              <Chip
                label={existingLead.status || 'New'}
                size="small"
                color="default"
                variant="outlined"
                sx={{ fontWeight: 500, height: 24, fontSize: '0.75rem' }}
              />
            }
          />
        </Box>
      </Box>

      {/* Notes Section */}
      {displayNotes && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="bold"
            letterSpacing={0.8}
            sx={{ display: 'block', mb: 1, textTransform: 'uppercase', fontSize: '0.75rem' }}
          >
            Notes
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {displayNotes}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Custom Fields Section */}
      {Object.keys(displayCustomFields).length > 0 && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="bold"
            letterSpacing={0.8}
            sx={{ display: 'block', mb: 1, textTransform: 'uppercase', fontSize: '0.75rem' }}
          >
            Additional Information
          </Typography>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            {Object.entries(displayCustomFields).map(([k, v], index, arr) => {
              const matchedCol = customColumns?.find((c) => c.key === k);
              const labelName = matchedCol?.name || k.replace(/^custom_/, '').replace(/_/g, ' ');
              const isCopyable = typeof v === 'string' && (matchedCol?.type === 'email' || matchedCol?.type === 'phone');

              return (
                <KeyValueRow
                  key={k}
                  label={labelName}
                  value={renderCustomFieldValue(v, matchedCol)}
                  copyableText={isCopyable ? String(v) : undefined}
                  isLast={index === arr.length - 1}
                />
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};
